import { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getCategories,
  newAccordance,
  updateAccordance,
  draftToApproval,
  getAccordanceType,
  getRootCauseCategories,
  getDueDate,
} from '../api/accordanceApi';
import { setDefaultValues } from '../components/ProjectDetail/ProjectTabs/Accordances/AccordanceCard/AccordanceCard';
import { getCalendarEvents } from '../redux/actions/calendar';
import { trackActionClicked } from '../api/trackActionClickedApi';
import { TrackedActions } from '../models/TrackedActionsEnum';
import { getSemesters } from '../components/ProjectDetail/ProjectTabs/Accordances/NewAccordanceForm/DateSelector';
import {
  validInputs,
  noErrors,
  updatedFormErrors,
  isValidPMBLink,
  isPotentialPMBLink,
  createCriticalIssuesList,
  createPossibleActionsList,
  revisionDateString,
  expirationDateString,
  generateRevisionDate,
  setCategoryName,
  accordanceDateString,
} from '../components/ProjectDetail/ProjectTabs/Accordances/NewAccordanceForm/NewAccordanceFormUtil';
import { AccordanceType } from '../models/AccordanceType';
import SelectOption from '../models/SelectOption';
import { getSupervisionInfo } from '../api/supervisionApi';
import { SortCriteria } from '../components/ProjectDetail/ProjectTabs/Supervision/util';
import { ProjectProductTypes } from '../models/ProjectProductTypes';
import { NewAccordanceContext } from '../components/Util/Modals/NewAccordance/context/NewAccordanceContext';
import { emptyBundleAccordance } from './useMultiAccordanceFormState';
import { getActiveSubstantialAndFundamentalChangeList } from '../api/contentManager';
import {
  SUCESSFUL_CREATE_NEW_ACCORDANCE,
  SUCESSFUL_SEND_FOR_APPROVAL_ACCORDANCE,
  ERROR_UNSAVED_ACCORDANCE,
} from '../components/Projects/ProjectsReport/utils/Constants/AlertStatusConstants';
import { AccordanceState } from '../models/AccordanceState';
import { createAccordanceAiMetrics } from '../services/accordanceAiMetrics';
import { trackUserpilotEvent } from '../services/userpilot';

const initialErrors = {
  criticalIssue: false,
  responsible: false,
  revisionDate: false,
  expirationDate: false,
  possibleAction: false,
  // semesterOption: false,
  accordanceDate: false,
  definedAgreement: false,
  linkedProductsAccordance: false,
  rootCauseOfCritical: false,
  rootCause: false,
  substantialTypeChange: false,
  pmbLink: false,
  pmbLinkInvalid: false,
};

const normalizeDateValue = (date?: Date | string | null) => {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
};

const createFormDirtySnapshot = ({
  criticalIssue,
  responsible,
  revisionDate,
  expirationDate,
  possibleAction,
  accordanceFiles,
  description,
  criticalIssueDescription,
  semesterOption,
  supervisionAction,
  accordanceDate,
  definedAgreement,
  linkedProductsAccordance,
  rootCauseOfCritical,
  rootCause,
  substantialTypeChange,
  PMBLink,
}) => {
  const normalizedFiles = Array.isArray(accordanceFiles) ? accordanceFiles : [];
  const normalizedLinkedProducts = Array.isArray(linkedProductsAccordance) ? linkedProductsAccordance : [];
  const normalizedSubstantialTypeChange = Array.isArray(substantialTypeChange) ? substantialTypeChange : [];

  return JSON.stringify({
    criticalIssueId: criticalIssue?.id ?? null,
    responsibleId: responsible?.id ?? null,
    revisionDate: normalizeDateValue(revisionDate),
    expirationDate: normalizeDateValue(expirationDate),
    possibleActionId: possibleAction?.id ?? null,
    accordanceFiles: normalizedFiles.map(file => file.url || file.name || '').sort(),
    description: (description || '').trim(),
    criticalIssueDescription: (criticalIssueDescription || '').trim(),
    semesterOptionValue: semesterOption?.value ?? null,
    supervisionActionId: supervisionAction?.id ?? null,
    accordanceDate: normalizeDateValue(accordanceDate),
    definedAgreementId: definedAgreement?.id ?? null,
    linkedProductsAccordance: normalizedLinkedProducts
      .map(item => item?.keyProductId ?? item?.id ?? null)
      .filter(item => item !== null)
      .sort(),
    rootCauseOfCriticalId: rootCauseOfCritical?.id ?? null,
    rootCauseId: rootCause?.id ?? null,
    substantialTypeChange: normalizedSubstantialTypeChange
      .map(change => change?.id ?? null)
      .filter(item => item !== null)
      .sort(),
    PMBLink: (PMBLink || '').trim(),
  });
};

const createInitialFormDirtySnapshot = (editAccordanceData, criticalDataForm) => {
  return createFormDirtySnapshot({
    criticalIssue: editAccordanceData?.criticalIssue
      ? { id: editAccordanceData.criticalIssue.id }
      : criticalDataForm
        ? { id: criticalDataForm.id }
        : null,
    responsible: editAccordanceData?.personInCharge ? { id: editAccordanceData.personInCharge.id } : null,
    revisionDate: editAccordanceData?.revisionDate || null,
    expirationDate: editAccordanceData?.expirationDate || null,
    possibleAction: editAccordanceData?.possibleAction ? { id: editAccordanceData.possibleAction.id } : null,
    accordanceFiles: editAccordanceData?.files || [],
    description: editAccordanceData?.description || '',
    criticalIssueDescription: editAccordanceData?.criticalIssueDescription || '',
    semesterOption: null,
    supervisionAction: editAccordanceData?.supervisionAction ? { id: editAccordanceData.supervisionAction.id } : null,
    accordanceDate: editAccordanceData?.accordanceDate || null,
    definedAgreement: editAccordanceData?.accordanceAgreementsType
      ? { id: editAccordanceData.accordanceAgreementsType.id }
      : null,
    linkedProductsAccordance: editAccordanceData?.linkedProductsAccordance || [],
    rootCauseOfCritical: editAccordanceData?.rootCauseCategory ? { id: editAccordanceData.rootCauseCategory.id } : null,
    rootCause: editAccordanceData?.rootCause ? { id: editAccordanceData.rootCause.id } : null,
    substantialTypeChange: editAccordanceData?.accordancesSubstantialFundamentalChanges?.map(change => ({
      id: change.substantialFundamentalChangesId,
    })) || [],
    PMBLink: decodeURIComponent(editAccordanceData?.urlPMB || ''),
  });
};

const useFormState = props => {
  const dispatch = useDispatch();
  const {
    accordanceFormType,
    displayGenericErrorsMessage,
    editAccordanceData,
    review,
    criticalDataForm,
    isSuggesttion,
    currentProject,
    accordanceState
  } = props;

  const [criticalIssue, setCriticalIssue] = useState<any>(criticalDataForm ? criticalDataForm : null);
  const [responsible, setResponsible] = useState<any>(null);
  const [revisionDate, setRevisionDate] = useState<any>(null);
  const [expirationDate, setExpirationDate] = useState<any>(null);
  const [category, setCategory] = useState<any>(null);
  const [possibleAction, setPossibleAction] = useState<any>(null);
  const [criticalIssueList, setCriticalIssuelist] = useState([]);
  const [possibleActionsList, setPossibleActionsList] = useState<any[]>([]);
  const [formErrors, setFormErrors] = useState<any>({ ...initialErrors });
  const [submitting, setSubmitting] = useState<any>(false);
  const [accordanceFiles, setAccordanceFiles] = useState<any[]>([]);
  const [description, setDescription] = useState('');
  const [criticalIssueDescription, setCriticalIssueDescription] = useState<any>('');
  const [semesterOption, setSemesterOption] = useState<any>(null);
  const [disableDraftButton, setDisableDraftButton] = useState(true);
  const [supervisionActionList, setSupervisionActionList] = useState<any[]>([]);
  const [supervisionAction, setSupervisionAction] = useState<any>(null);
  const [accordanceDate, setAccordanceDate] = useState<any>(null);
  const [bundleAccordances, setBundleAccordances] = useState<any[]>([]);

  const [definedAgreement, setDefinedAgreement] = useState<any>(null);
  const [linkedProductsAccordance, setLinkedProductsAccordance] = useState<any>(null);
  const [rootCauseOfCritical, setRootCauseOfCritical] = useState<any>(null);
  const [rootCause, setRootCause] = useState<any>(null);

  const [accordanceType, setAccordanceType] = useState<any>([]);
  const [rootCauseCategories, setRootCauseCategories] = useState<any>([]);
  const [rootCauseForm, setRootCauseForm] = useState<any>([]);
  const [productForm, setProductForm] = useState<any>([]);

  const [substantialTypeChange, setSubstantialTypeChange] = useState<any>([]);
  const [PMBLink, setPMBLink] = useState<any>("");
  const [substantialChangeTypeOptions, setSubstantialChangeTypeOptions] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [alertConfig, setAlertConfig] = useState<any>();

  const [isFirstCharge, setIsFirstCharge] = useState(true);
  const [isRequiredChangeRevisionDate, setIsRequiredChangeRevisionDate] = useState(false);
  const aiMetricsRef = useRef(createAccordanceAiMetrics());

  const externalSetAlertConfig = props.setAlertConfig;
  const updateAlertConfig = externalSetAlertConfig || setAlertConfig;

  const { t } = useTranslation();

  const editAccordanceInfo = setDefaultValues(props.editAccordanceData, t);

  const { setState, state } = useContext(NewAccordanceContext);

  useEffect(() => {
    if (noErrors(formErrors)) {
      displayGenericErrorsMessage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors]);

  const displayErrors = () => {
    const newFormErrors = updatedFormErrors(
      criticalIssue,
      responsible,
      expirationDate,
      // possibleAction,
      revisionDate,
      props.accordanceFormType,
      // semesterOption,
      // accordanceDate,
      definedAgreement,
      linkedProductsAccordance,
      rootCauseOfCritical,
      rootCause,
      criticalIssueDescription,
      description,
      substantialTypeChange,
      PMBLink,
    );

    setFormErrors(newFormErrors);
    displayGenericErrorsMessage(true);
  };

  const closeAndRefreshData = () => {
    setSubmitting(false);
    props.close({ refreshData: true, isDraft: false });
  };

  const submitAccordance = async (publish = false) => {
    setLoading(true);
    const linkedProductData = linkedProductsAccordance?.map(element => {
      return {
        id: null,
        name: null,
        projectId: null,
        componentOrder: null,
        productOrder: null,
        keyProductId: element.keyProductId || element.id,
      };
    });

    const data = {
      projectId: props.currentProject.id,
      revisionDate: revisionDateString(revisionDate),
      expirationDate: expirationDate ? expirationDateString(expirationDate) : null,
      accordanceType: definedAgreement.id,
      criticalIssueId: criticalIssue.id,
      personInChargeId: responsible?.id ?? null,
      possibleActionId: possibleAction?.id ? possibleAction.id : null,
      files: accordanceFiles,
      revisionId: review ? review.id : undefined,
      description,
      draft: false,
      criticalIssueDescription,
      supervisionActionId: supervisionAction && supervisionAction.id,
      accordanceDate: accordanceDate ? accordanceDateString(accordanceDate) : null,
      rootCauseCategoryId: rootCauseOfCritical?.id,
      rootCauseId: rootCause?.id,
      linkedProductsAccordance: linkedProductData === undefined ? [] : linkedProductData,
      urlPMB: encodeURIComponent(PMBLink),
      substantialFundamentals: substantialTypeChange.map(change => {
        return {
          substantialFundamentalChangesId: change.id
        };
      })
    };

    setSubmitting(true);
    try {
      if (editAccordanceData && !publish) {
        await updateAccordance({ ...data, accordanceId: editAccordanceData.id });
        updateAlertConfig(SUCESSFUL_CREATE_NEW_ACCORDANCE);
        closeAndRefreshData();
      } else if (editAccordanceData && publish === true) {
        await updateAccordance({ ...data, accordanceId: editAccordanceData.id });
        await draftToApproval(editAccordanceData.id);
        updateAlertConfig(SUCESSFUL_SEND_FOR_APPROVAL_ACCORDANCE);
        closeAndRefreshData();
      } else {
        await newAccordance(data);
        dispatch(getCalendarEvents(props.currentProject.id));
        updateAlertConfig(SUCESSFUL_SEND_FOR_APPROVAL_ACCORDANCE);
        closeAndRefreshData();
      }
    } catch (error) {
      closeAndRefreshData();
      updateAlertConfig(ERROR_UNSAVED_ACCORDANCE)
      displayGenericErrorsMessage(true);
      setSubmitting(false);
    }
    finally {
      setLoading(false);
    }
  };

  const isValidInputs = () => {
    return validInputs(
      criticalIssue,
      responsible,
      expirationDate,
      revisionDate,
      props.accordanceFormType,
      definedAgreement,
      linkedProductsAccordance,
      rootCauseOfCritical,
      rootCause,
      criticalIssueDescription,
      description,
      substantialTypeChange,
      PMBLink,
    )
  };

  const disableSendToApprovalButton = !isValidInputs();

  const submit = (publish = false) => {
    if (isValidInputs()) {
      trackAiUse(!editAccordanceData || publish);
      submitAccordance(publish);
      trackActionClicked(TrackedActions.createAccordanceClicked);
    } else {
      displayErrors();
    }
  };

  const submitAccordanceDraft = async () => {
    trackAiUse(false);
    setLoading(true);
    const linkedProductData = linkedProductsAccordance?.map(element => {
      return {
        id: null,
        name: null,
        projectId: null,
        componentOrder: null,
        productOrder: null,
        keyProductId: element.keyProductId || element.id,
      };
    });

    const data = {
      projectId: props.currentProject.id,
      revisionDate: revisionDate,
      expirationDate: expirationDate ? expirationDateString(expirationDate) : null,
      accordanceType: definedAgreement?.id,
      criticalIssueId: criticalIssue ? criticalIssue.id : null,
      personInChargeId: responsible ? responsible.id : null,
      possibleActionId: possibleAction ? possibleAction.id : null,
      files: accordanceFiles,
      revisionId: review ? review.id : null,
      description,
      criticalIssueDescription,
      supervisionActionId: supervisionAction && supervisionAction.id,
      draft: true,
      accordanceDate: accordanceDate ? accordanceDateString(accordanceDate) : null,
      issuggestedAgreement: isSuggesttion !== undefined ? true : false,
      rootCauseCategoryId: rootCauseOfCritical?.id,
      rootCauseId: rootCause?.id,
      linkedProductsAccordance: linkedProductData === undefined ? [] : linkedProductData,
      urlPMB: encodeURIComponent(PMBLink),
      substantialFundamentals: substantialTypeChange.map(change => {
        return {
          substantialFundamentalChangesId: change.id
        };
      })
    };

    try {
      if (editAccordanceData) {
        await updateAccordance({ ...data, accordanceId: editAccordanceData.id });
        setSubmitting(false);
        props.close({ refreshData: true, isDraft: false });
        updateAlertConfig(SUCESSFUL_CREATE_NEW_ACCORDANCE);
      } else {
        await newAccordance(data);
        dispatch(getCalendarEvents(props.currentProject.id));
        setSubmitting(false);
        props.close({ refreshData: true, isDraft: true });
        updateAlertConfig(SUCESSFUL_CREATE_NEW_ACCORDANCE);
      }
    } catch (error) {
      props.close({ refreshData: true });
      updateAlertConfig(ERROR_UNSAVED_ACCORDANCE)
      console.error('Error submitting draft accordance:', error);
      displayGenericErrorsMessage(true);
      setSubmitting(false);
      setFormErrors({ ...formErrors, ...{ definedAgreement: true } });
    }
    finally {
      setLoading(false);
    }
  };

  const handleDefinedAgreementChange = async (event: any, item: any) => {
    setFormErrors({
      ...formErrors,
      ...{ definedAgreement: false, substantialTypeChange: false, pmbLink: false, pmbLinkInvalid: false },
    });

    if (!item || item.id === '-') {
      setDefinedAgreement(null);
    } else {
      setDefinedAgreement(item);
    }
  };

  const validateTextareaCritical = (value: any) => {
    setCriticalIssueDescription(value);
    if (value.length <= 3 || !value) {
      setFormErrors({ ...formErrors, ...{ criticalIssueDescription: true } });
    } else {
      setFormErrors({ ...formErrors, ...{ criticalIssueDescription: false } });
    }
  };

  const validateTextareaDescription = (value: any) => {
    setDescription(value);
    if (value.length <= 3 || !value) {
      setFormErrors({ ...formErrors, ...{ description: true } });
    } else {
      setFormErrors({ ...formErrors, ...{ description: false } });
    }
  };

  const handleLinkedProduct = async (event: any, item: any) => {
    setFormErrors({ ...formErrors, ...{ linkedProductsAccordance: false } });

    if (!item || item.id === '-') {
      setLinkedProductsAccordance(null);
    } else {
      setLinkedProductsAccordance(item);
    }
  };

  const handleRootCauseOfCritical = async (event: any, item: any) => {
    setFormErrors({ ...formErrors, ...{ rootCauseOfCritical: false } });
    if (!item || item.id === '-') {
      setRootCauseOfCritical(null);
    } else {
      setRootCauseForm(rootCauseCategories?.find(val => val.name === item.name).rootCause);
      setRootCauseOfCritical(item);
      setRootCause(null);
    }
  };

  const handleRootCause = async (event: any, item: any) => {
    setFormErrors({ ...formErrors, ...{ rootCause: false } });

    if (!item || item.id === '-') {
      setRootCause(null);
    } else {
      setRootCause(item);
    }
  };

  const handleCriticalIssueChange = async (event: any, item: any) => {
    const productTypeParam =
      props.currentProject.projectProductType === ProjectProductTypes.CT ? ProjectProductTypes.CT : undefined;
    handleDisableDraftButton(item);
    setFormErrors({ ...formErrors, ...{ criticalIssue: false } });
    const previousCriticalIssue = criticalIssue;

    if (!item || item.id === '-') {
      setCriticalIssue(null);
      setCategory('');
    } else {
      setProductForm(item.keyProducts);
      setLinkedProductsAccordance(null);
      setCriticalIssue(item);
      setCategory(setCategoryName(item));

      const categories = await getCategories(productTypeParam, props.currentProject.execution, props.currentProject.id);
      const category = categories.find(({ id }) => id === item.categoryId);

      const possibleActions = category &&
        category.criticalIssues &&
        item.value && [...category.criticalIssues.find(ci => ci.id === parseInt(item.value)).possibleActions];

      setPossibleActionsList(createPossibleActionsList(possibleActions));
    }

    previousCriticalIssue && setPossibleAction(null);
  };

  const handlePossibleActionChange = (item: SelectOption) => {
    item && item.label === '-' ? setPossibleAction(null) : setPossibleAction(item);
    setFormErrors({ ...formErrors, ...{ possibleAction: false } });
  };

  const handleSupervisionActionChange = (event: any, item: SelectOption) => {
    handleDisableDraftButton(true);
    setSupervisionAction(item);
  };

  const getData = async () => {
    const productTypeParam =
      props.currentProject.projectProductType === ProjectProductTypes.CT ? ProjectProductTypes.CT : undefined;
    const categories = await getCategories(productTypeParam, props.currentProject.execution, props.currentProject.id);


    let accordanceTypeForm = await getAccordanceType();

    // TODO: This implementation only activate when Focal Group and client has approve his publish to prod
    // const substantialIndex = accordanceTypeForm.findIndex(item => item.id === AccordanceType.Substantial);
    // if (substantialIndex > -1) {
    //   const [substantialItem] = accordanceTypeForm.splice(substantialIndex, 1);
    //   accordanceTypeForm.splice(1, 0, substantialItem);
    // }
    accordanceTypeForm = accordanceTypeForm.filter(item => item.id !== AccordanceType.Substantial);

    setAccordanceType(accordanceTypeForm);

    const rootCauseCategories = await getRootCauseCategories();
    setRootCauseCategories(rootCauseCategories);

    const criticalIssuesList = createCriticalIssuesList(categories);
    setCriticalIssuelist(criticalIssuesList);
  };

  const getActivitiesAndCriticalProducts = async () => {
    const info = await getSupervisionInfo(props.currentProject.id, null, '', 1, SortCriteria.Default, false, 200);
    const infoOptions =
      info && info.paginatedItems
        ? info.paginatedItems.collection.map(activity => ({
          id: activity.id,
          label: activity.description,
          value: activity.description,
        }))
        : [];
    setSupervisionActionList(infoOptions || []);
  };

  useEffect(() => {
    getData();
    getActivitiesAndCriticalProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIemSelected = (selected: any) => {
    handleDisableDraftButton(selected);
    setResponsible(selected);
    setFormErrors({ ...formErrors, ...{ responsible: false } });
  };

  const handleExpirationDate = date => {
    setFormErrors({ ...formErrors, ...{ expirationDate: false } });
    setExpirationDate(date);
  };

  const populateForm = editAccordanceData => {
    const nextRevisionDate = generateRevisionDate(editAccordanceData);
    const nextExpirationDate = editAccordanceData.expirationDate ? new Date(editAccordanceData.expirationDate) : null;
    const nextCriticalIssue = editAccordanceData.criticalIssue
      ? {
        ...editAccordanceData.criticalIssue,
        value: editAccordanceData.criticalIssue.id.toString(),
        label: editAccordanceData.criticalIssue.name,
      }
      : null;
    const nextPossibleAction = editAccordanceData.possibleAction
      ? {
        id: editAccordanceData.possibleAction.id,
        label: editAccordanceData.possibleAction.name,
        value: editAccordanceData.possibleAction.id.toString(),
      }
      : null;
    const nextDefinedAgreement = editAccordanceData.accordanceAgreementsType || null;
    const nextLinkedProductsAccordance = editAccordanceData.linkedProductsAccordance || null;
    const nextDescription = editAccordanceData.description || '';
    const nextResponsible = editAccordanceData.personInCharge || null;
    const nextCriticalIssueDescription = editAccordanceData.criticalIssueDescription || '';
    const nextRootCauseOfCritical = editAccordanceData.rootCauseCategory || null;
    const nextRootCause = editAccordanceData.rootCause || null;
    const nextPMBLink = decodeURIComponent(editAccordanceData.urlPMB || '');
    const nextSubstantialTypeChange =
      editAccordanceData.accordancesSubstantialFundamentalChanges?.map(change => ({
        id: change.substantialFundamentalChangesId,
        name: change.nameSubstantialFundamentalChanges,
      })) || [];

    setRevisionDate(nextRevisionDate);

    if (accordanceState === AccordanceState.REJECT) {
      setIsRequiredChangeRevisionDate(true);
    }

    if (nextCriticalIssue) {
      handleCriticalIssueChange(null, nextCriticalIssue);
    } else {
      setCriticalIssue(null);
      setCategory('');
      setProductForm([]);
      setPossibleActionsList([]);
    }

    if (nextPossibleAction) {
      handlePossibleActionChange(nextPossibleAction);
    } else {
      setPossibleAction(null);
    }
    setExpirationDate(nextExpirationDate);
    setDefinedAgreement(nextDefinedAgreement);
    setLinkedProductsAccordance(nextLinkedProductsAccordance);
    setDescription(nextDescription);
    setResponsible(nextResponsible);
    setCriticalIssueDescription(nextCriticalIssueDescription);
    setRootCauseOfCritical(nextRootCauseOfCritical);
    setRootCause(nextRootCause);
    handleSupervisionActionChange(
      null,
      editAccordanceData.supervisionAction && {
        id: editAccordanceData.supervisionAction.id,
        value: editAccordanceData.supervisionAction.description,
        label: editAccordanceData.supervisionAction.description,
      },
    );
    setPMBLink(nextPMBLink);
    setSubstantialTypeChange(nextSubstantialTypeChange);

    setFormErrors(
      updatedFormErrors(
        nextCriticalIssue,
        nextResponsible,
        nextExpirationDate,
        nextRevisionDate,
        props.accordanceFormType,
        nextDefinedAgreement,
        nextLinkedProductsAccordance,
        nextRootCauseOfCritical,
        nextRootCause,
        nextCriticalIssueDescription,
        nextDescription,
        nextSubstantialTypeChange,
        nextPMBLink,
      ),
    );
  };

  const handleAccordanceDate = date => {
    setFormErrors({ ...formErrors, ...{ accordanceDate: false } });
    setAccordanceDate(date);
  };

  useEffect(() => {
    if (editAccordanceData) {
      getData();
      populateForm(editAccordanceData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAccordanceData]);

  useEffect(() => {
    revisionDate ||
      expirationDate ||
      semesterOption ||
      description ||
      criticalIssueDescription ||
      accordanceFiles.length
      ? setDisableDraftButton(false)
      : setDisableDraftButton(true);
  }, [revisionDate,
    expirationDate,
    semesterOption,
    description,
    criticalIssueDescription,
    PMBLink,
    linkedProductsAccordance,
    rootCauseOfCritical,
    rootCause,
    substantialTypeChange,
    accordanceFiles]);

  useEffect(() => {
    const fetchDueDate = async () => {
      if (revisionDate && definedAgreement?.id === AccordanceType.Substantial) {
        const dateString = revisionDate.toISOString().split('T')[0];
        const newDate = await getDueDate(dateString);
        setExpirationDate(new Date(newDate));
        setIsFirstCharge(false);
      }
    };

    fetchDueDate();

    if (!isFirstCharge && editAccordanceData && editAccordanceData.revisionDate) {
      setIsRequiredChangeRevisionDate(false);
    }
  }, [revisionDate, definedAgreement]);

  useEffect(() => {
    handleSubstantialChangeTypeOptions();
  }, [currentProject])

  const initialDirtySnapshot = createInitialFormDirtySnapshot(editAccordanceData, criticalDataForm);
  const currentDirtySnapshot = createFormDirtySnapshot({
    criticalIssue,
    responsible,
    revisionDate,
    expirationDate,
    possibleAction,
    accordanceFiles,
    description,
    criticalIssueDescription,
    semesterOption,
    supervisionAction,
    accordanceDate,
    definedAgreement,
    linkedProductsAccordance,
    rootCauseOfCritical,
    rootCause,
    substantialTypeChange,
    PMBLink,
  });
  const isFormDirty = initialDirtySnapshot !== currentDirtySnapshot;

  const trackAiUse = (isSentForApproval: boolean) => {
    trackUserpilotEvent('ia_use', aiMetricsRef.current.buildProperties({
      projectId: props.currentProject.id,
      isSaved: isFormDirty,
      isDraft: Boolean(
        editAccordanceData &&
        (accordanceState === AccordanceState.DRAFT || editAccordanceData.accordanceState === AccordanceState.DRAFT),
      ),
      isEdited: Boolean(editAccordanceData),
      isSentForApproval,
      accordanceText: description || '',
      criticalIssueText: criticalIssueDescription || '',
    }));
  };

  const handleDisableDraftButton = (item: any) => (item ? setDisableDraftButton(false) : setDisableDraftButton(true));

  const setMultipleAccordances = () => {
    if (
      validInputs(
        criticalIssue,
        responsible,
        expirationDate,
        // possibleAction,
        revisionDate,
        props.accordanceFormType,
        // semesterOption,
        definedAgreement,
        linkedProductsAccordance,
        rootCauseOfCritical,
        rootCause,
        criticalIssueDescription,
        description,
        substantialTypeChange,
        PMBLink,
      )
    ) {
      const first = {
        internalId: new Date().getTime().toString(),
        projectId: props.currentProject.id,
        revisionDate: semesterOption && semesterOption.value ? new Date(semesterOption.value) : revisionDate,
        semesterOption,
        criticalIssue,
        criticalIssueDescription,
        expirationDate,
        responsible,
        // possibleAction,
        accordanceFiles,
        definedAgreement,
        linkedProductsAccordance,
        rootCauseOfCritical,
        rootCause,
        files: [...accordanceFiles],
        review,
        description,
        draft: false,
        supervisionAction,
        formErrors: {
          criticalIssue: false,
          responsible: false,
          revisionDate: false,
          expirationDate: false,
          // possibleAction: false,
          // semesterOption: false,
          definedAgreement: false,
          linkedProductsAccordance: false,
          rootCauseOfCritical: false,
          rootCause: false,
          criticalIssueDescription: false,
          description: false,
          substantialTypeChange: false,
          pmbLink: false,
          pmbLinkInvalid: false,
        },
        category,
      };
      const newAccordance = {
        ...first,
        internalId: (new Date().getTime() + 10).toString(),
        ...emptyBundleAccordance,
      };
      setBundleAccordances([first, newAccordance]);
      setState({
        ...state,
        bundleAccordances: [first, newAccordance],
        accordanceFormType: definedAgreement.id,
        possibleActionsList,
        supervisionActionList,
      });
    } else {
      displayErrors();
    }
  };

  const handleSubstantialTypeChange = (event: any, item: any) => {
    const hasValue = Array.isArray(item) && item.length > 0;
    setFormErrors({ ...formErrors, ...{ substantialTypeChange: !hasValue } });
    setSubstantialTypeChange(item);
  };

  const handlePMBLink = (link: string, validateStrict = false) => {
    const hasValue = !!link?.trim();
    const isInvalid = hasValue && (validateStrict ? !isValidPMBLink(link) : !isPotentialPMBLink(link));
    setFormErrors({ ...formErrors, ...{ pmbLink: !hasValue, pmbLinkInvalid: isInvalid } });
    setPMBLink(link);
  };

  const handleSubstantialChangeTypeOptions = () => {
    getActiveSubstantialAndFundamentalChangeList()
      .then(options => {
        const filteredOptions = options.filter(option => option.sovereignOperationsId === currentProject.sovereignOperationsId);
        setSubstantialChangeTypeOptions(filteredOptions);
      })
      .catch(error => {
        console.error('Error fetching substantial and fundamental change list:', error);
      });
  }

  return {
    accordanceFormType,
    revisionDate,
    setFormErrors,
    formErrors,
    setRevisionDate,
    expirationDate,
    setExpirationDate,
    setSemesterOption,
    semesterOption,
    criticalIssueList,
    criticalIssue,
    handleCriticalIssueChange,
    category,
    possibleActionsList,
    possibleAction,
    handlePossibleActionChange,
    handleExpirationDate,
    responsible,
    handleIemSelected,
    accordanceFiles,
    setAccordanceFiles,
    editAccordanceInfo,
    description,
    criticalIssueDescription,
    setCriticalIssueDescription,
    setDescription,
    submit,
    disableDraftButton,
    disableSendToApprovalButton,
    submitting,
    submitAccordanceDraft,
    alertConfig,
    setAlertConfig,
    supervisionActionList,
    handleSupervisionActionChange,
    supervisionAction,
    accordanceDate,
    handleAccordanceDate,
    bundleAccordances,
    setBundleAccordances,
    setMultipleAccordances,
    definedAgreement,
    linkedProductsAccordance,
    rootCauseOfCritical,
    rootCause,
    handleDefinedAgreementChange,
    handleLinkedProduct,
    handleRootCauseOfCritical,
    handleRootCause,
    accordanceType,
    rootCauseCategories,
    rootCauseForm,
    productForm,
    validateTextareaCritical,
    validateTextareaDescription,
    substantialTypeChange,
    handleSubstantialTypeChange,
    PMBLink,
    handlePMBLink,
    substantialChangeTypeOptions,
    loading,
    setLoading,
    isFormDirty,
    aiMetrics: aiMetricsRef.current,
    isRequiredChangeRevisionDate
  };
};

export default useFormState;
